import React from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Tag, Typography } from 'antd';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkMath from 'remark-math';
import 'katex/dist/katex.min.css';

export type PillarTwoGuideKey =
  | 'portfolio-fit'
  | 'position-direction'
  | 'position-size'
  | 'option-choice'
  | 'own-view-consistency'
  | 'limit-discipline'
  | 'risk-limits';

const PORTFOLIO_FIT = String.raw`
This score is calculated separately for **each user and each period**. It
compares that user's portfolio with the period's optimal portfolio across all
in-play instruments at once.

Here, a **vector** means an ordered list with one value for every instrument.
The portfolio's **pattern** means which instruments have exposure, whether each
exposure is long or short, and their relative sizes after risk scaling. It is
not a price pattern over time.

### 1. Build the two vectors

For each instrument $i$, the assessment calculates:

$$
\mathrm{UserValue}_i=
\frac{\mathrm{UserExposure}_i}{\mathrm{InstrumentRiskLimit}_i}
$$

$$
\mathrm{OptimalValue}_i=
\frac{\mathrm{OptimalExposure}_i}{\mathrm{InstrumentRiskLimit}_i}
$$

Positive values are long, negative values are short, and zero means flat. The
user values form one vector; the optimal values form the other.

This uses the **per-instrument risk limit** shown in the Risk limits tab.
Whole-portfolio limits, such as the combined bond limit, are not used as
divisors here. They constrain the optimal portfolio and are checked separately
by Limit discipline.

### 2. Compare the two vectors

The assessment uses **cosine similarity** to measure how closely the two vectors
point in the same direction:

$$
\mathrm{Cosine}=
\frac{\mathrm{User}\cdot\mathrm{Optimal}}
{\lVert\mathrm{User}\rVert\,\lVert\mathrm{Optimal}\rVert}
$$

The dot means “multiply matching values and add the results.” The double bars
mean the vector's length, calculated from all its values. The result is changed
from a range of $-1$ to $1$ into a score from $0$ to $1$:

$$
\mathrm{PatternScore}=\frac{\mathrm{Cosine}+1}{2}
$$

- The same directions and relative sizes score $100\%$.
- Unrelated patterns score $50\%$.
- An exactly opposite pattern scores $0\%$.
- If both portfolios are flat, the score is $100\%$.
- If only one portfolio is flat, the score is $50\%$.

For example, if the optimal vector is $[+0.50,-0.50]$:

- $[+0.25,-0.25]$ scores $100\%$: it has the same pattern at half the size.
- $[+0.50,+0.50]$ scores $50\%$: one side agrees and one conflicts.
- $[-0.25,+0.25]$ scores $0\%$: every direction is reversed.

This comparison measures relative risk-scaled proportions, not absolute size.
Position size separately checks how closely each instrument's exposure matches
the optimal exposure. Because each instrument can have a different risk limit,
the limits also control how strongly each instrument affects this comparison.

### 3. Convert it to Pillar 2 points

$$
\mathrm{PortfolioFitPoints}=35\times\mathrm{PatternScore}.
$$

### How the optimal portfolio is computed

For each period, the assessment turns the benchmark's combined news view
(PCS) and the agreement between its news signals into a desired direction and
exposure for each instrument. Options are considered only when their payoff
matches the view and their estimated benefit justifies their premium.

The assessment then finds the portfolio closest to those desired exposures
that stays within the instrument, per-option premium, whole-portfolio, and
short-pool limits. Those final positions are the optimal portfolio used in the
comparison above.
`;

const POSITION_DIRECTION = String.raw`
This score checks whether each user position is on the same side as its optimal
position for the period.

Both positions are measured as a share of the instrument's **risk limit**: the
position-size limit selected for that assessment. The Risk limits tab explains
where this number comes from.

### Score each instrument

- Both positions flat: $100\%$.
- Both long, or both short: $100\%$.
- One long and one short: $0\%$.
- Only one position flat: partial credit. The score falls to zero when the
  non-flat position reaches $50\%$ of its risk limit.

Large user or optimal positions carry more weight than small positions. A
minimum $5\%$ weight keeps near-flat instruments in the calculation.

### Convert it to Pillar 2 points

The assessment takes the weighted average across instruments:

$$
\mathrm{DirectionPoints}=20\times\mathrm{DirectionScore}.
$$

Only the side is checked here. The amount is scored in Position size.
`;

const POSITION_SIZE = String.raw`
This score checks whether the user took about the same amount of risk as the
optimal portfolio. Long and short signs are ignored here because Position
direction scores them separately.

### Compare size as a share of the limit

For each instrument:

- $u$ is the absolute user position divided by its risk limit.
- $t$ is the absolute optimal position divided by its risk limit.
- The risk limit is the position-size limit selected for that assessment. The
  Risk limits tab explains where this number comes from.

The instrument score is

$$
q=\frac{\min(u+0.03,t+0.03)}{\max(u+0.03,t+0.03)}.
$$

An exact size match scores $100\%$. The added $0.03$, equal to $3\%$ of the
risk limit, prevents tiny or flat positions from creating an unstable ratio.

Larger user or optimal positions carry more weight. A minimum $5\%$ weight
keeps near-flat instruments in the calculation.

### Convert it to Pillar 2 points

$$
\mathrm{PositionSizePoints}=20\times\mathrm{SizeScore}.
$$
`;

const OPTION_CHOICE = String.raw`
This score checks whether the user used options when the optimal portfolio
called for them, avoided unnecessary options, and chose a suitable size.

An option is a position whose payoff depends on another instrument, called its
**underlying instrument**. **PCS — Period Conviction Score** is the benchmark
view for that underlying instrument on the $-5$ to $+5$ conviction scale.

### Score each option

- Let $u$ be the user's option size as a share of its risk limit, and let $t$ be
  the optimal option size on the same scale. The risk limit is the position-size
  limit selected for that assessment.
- If neither portfolio uses the option: $100\%$.
- If the optimal portfolio uses it, the assessment starts with

  $$
  q=\max\!\left(0,\min\!\left(1,1-\frac{|u-t|}{t+0.15}\right)\right).
  $$

  The added $0.15$ allows a difference equal to $15\%$ of the risk limit.
  Not using the option still receives at least $40\%$, because the view may be
  expressed without an option.
- If the optimal portfolio does not use it: the score falls to zero when the
  user's option reaches $50\%$ of its risk limit.
- If the user holds an option but PCS does not pass $1.5$ in the direction
  where that option gains, the result is multiplied by $0.60$.

Option scores are weighted by the larger user or optimal size, with a minimum
$10\%$ weight. If the scenario has no option instruments, this component
receives full credit.

$$
\mathrm{OptionChoicePoints}=10\times\mathrm{OptionChoiceScore}.
$$
`;

const OWN_VIEW_CONSISTENCY = String.raw`
This score checks whether the user's positions agree with the user's own
conviction sliders. It does not compare the sliders with the benchmark view.

The assessment uses the last saved slider for each instrument. If it was never moved, its
value is zero. Position size is measured as a share of the instrument's risk
limit: the position-size limit selected for that assessment. The Risk limits
tab explains where this number comes from.

### Decide the expected position side

- For ordinary price-based instruments, a positive slider expects a long
  position and a negative slider expects a short position.
- For yield-based bonds, this is reversed: a positive yield view expects a
  short bond position.
- An option expects a long position only when its underlying slider passes
  $1.5$ in the direction where the option gains. Otherwise it expects no
  option position.

### Score the position

- A position at or below $2.5\%$ of its risk limit receives full credit.
- Above that level, matching the expected side receives full credit and the
  opposite side receives none.
- When the slider expects a flat position, credit falls to zero as the position
  reaches $50\%$ of its risk limit.

Larger positions carry more weight, with a minimum $5\%$ weight.

$$
\mathrm{OwnViewPoints}=7.5\times\mathrm{OwnViewScore}.
$$
`;

const LIMIT_DISCIPLINE = String.raw`
This score checks whether the user's complete portfolio stayed within the
session's risk limits.

The checks cover total and bond exposure, bond interest-rate sensitivity,
equity exposure, foreign-exchange exposure, concentrated US-dollar exposure,
commodity exposure, option premium, estimated loss in a fixed adverse market
test, and the short-position limit when the session provides one.

### Measure each breach

For every breached limit, the assessment calculates the excess as a share of that limit:

$$
\mathrm{Excess}=\frac{|\mathrm{Amount}|-\mathrm{Limit}}{\mathrm{Limit}}.
$$

Values inside a limit add zero. The adverse-market loss breach has twice the
normal weight; net bond interest-rate sensitivity has $1.2$ times the normal
weight; total and bond exposure have $0.8$ times the normal weight. Other
checks have normal weight.

The assessment adds the weighted breaches into one total, $E$:

$$
\mathrm{LimitScore}=\max\!\left(0,\min\!\left(1,1-\frac{E}{3}\right)\right).
$$

No breaches score $100\%$. A weighted breach total of $3$ or more scores zero.

$$
\mathrm{LimitDisciplinePoints}=7.5\times\mathrm{LimitScore}.
$$
`;

const RISK_LIMITS = String.raw`
A **risk limit** is a number the assessment uses to measure a position or check
the complete portfolio. It is a scoring input.

### Instrument risk limits

An instrument's **risk limit** is the position-size scale used by Overall
portfolio fit, Position direction, Position size, Option choice, and Own-view
consistency:

$$
\mathrm{PositionShare}=\frac{\mathrm{Position}}{\mathrm{RiskLimit}}.
$$

Assessment selects one value for each assessment instrument. These are the possible
sources, from highest to lowest priority:

1. **Linked session settings (dynamic per session) — the normal source.** Unless the assessment uses
   the **"reference framework"**—the mode that keeps stored predefined assessment limits—PM
   derives a limit from the linked session. With hard limits on:

   $$
   \mathrm{SessionLimit}=\frac{\mathrm{StartingCapital}\times
   \mathrm{AssetClassLimit\%}}{\mathrm{MarginFraction}}.
   $$

   The asset-class limit is the percentage configured for the instrument's
   class. PM copies that percentage onto every player's instrument, and the
   assessment uses it as risk limit input. Assessment limits factor in PM margin.
   Assessment limits also factor in PM's "soft/hard limits" setting (hard: per asset class %limit * AUM * margin; soft: 100% AUM * margin)

2. **Stored assessment definition.** Each assessment instrument stores a
   limit. It may have been entered directly, imported with the assessment, or
   copied when the assessment was cloned. The original Turn instruments were
   filled from the engine's fixed reference table.

3. **Derived assessment default.** If a stored instrument has no limit, assessment
   derives one from its risk type. Bond limits come from an interest-rate-risk
   budget. Other instruments use an estimated adverse price move and a loss
   budget.

4. **Final code fallback.** If no usable stored or derived value exists, the
   code uses $1$. This only keeps the calculation running; it is not a meaningful
   configured limit, and a valid published assessment should not depend on it.

### Types of limits the assessment uses

- **Instrument limit.** Maximum optimizer usage and the scale used to compare
  user and optimal positions. It normally comes from the selected PM session.
  Otherwise, it uses the assessment's stored value, a derived assessment
  default, or finally the $1$ code fallback described above.
- **Per-option premium limit.** Limits an individual option in the optimizer.
  It comes from the option's stored or derived assessment setting. If none is
  set, the assessment uses the total option-premium limit.
- **Whole-portfolio limits.** “Whole portfolio” means **one player’s complete
  portfolio during one assessment period**. It never combines different players.
  - The thresholds are resolved once for the selected assessment-session
    combination and shared by all assessed players and periods.
  - Each player’s positions are aggregated separately for each period.
  - That player-period aggregate is compared with the shared threshold.
  - The period’s optimal portfolio is also constrained by those same thresholds.
- **Short pool.** Combined short-position capacity, measured using each
  instrument class's margin. It comes from the PM session's starting
  capital, short-selling limit, and class margins.

### Exact source of every limit

- **Instrument limit.** Normally derived dynamically from the selected PM
  session: starting capital, hard/soft setting, instrument-limit percentage,
  and class margin. If unavailable or the reference framework is selected, it
  uses the assessment instrument’s stored/derived value; final fallback is $1$.
- **Per-option premium limit.** Comes from the assessment instrument’s stored
  **premium_cap**. It may originally have been derived when the assessment was
  created. If absent, it uses the total option-premium limit below. It does not
  come from a PM per-option setting.
- **Total gross positions.** Dynamically derived from the session’s capital,
  class margins, represented asset classes, and FX capacity. Falls back to the
  assessment engine’s standard limit if session limits cannot be resolved.
- **Combined bonds.** Dynamically derived from session capital and bond margin.
  Otherwise uses the engine’s standard limit.
- **Net DV01.** Uses the PM session’s trainer-set net-DV01 limit when present.
  Otherwise uses the engine’s standard value, normally scaled to session capital.
- **Gross DV01.** Uses the PM session’s trainer-set gross-DV01 limit when present.
  Otherwise uses the engine’s standard value, normally scaled to session capital.
- **Net and gross equity delta.** Assessment-engine standard values, scaled to
  session capital.
- **Combined FX.** Dynamically calculated as session capital × session FX
  leverage. If no usable FX setting exists, it uses the engine’s standard value.
- **Repeated/same-direction USD exposure.** Assessment-engine standard value,
  scaled to session capital.
- **Combined commodities.** Assessment-engine standard value, scaled to session
  capital.
- **Total option premium.** Assessment-engine standard value, scaled to session
  capital.
- **Stress loss.** Assessment-engine standard value—15% of capital—scaled to
  session capital.
- **Short pool.** Dynamically derived only from session capital, short-selling
  capacity, and class margins. It has no assessment-model fallback; the check is
  omitted when it cannot be resolved.

-----
Unverified ai explanations:
If one assessment code represents several PM assets, their session limits are
normally added. A rolling-bond family—a set of replacement bond issues that
represents one assessment instrument—uses one limit when only one issue can be
active at a time. The session override is applied one instrument at a time, so
an instrument with no session value keeps its stored or derived assessment
value.

### Complete-portfolio limits

Limit discipline and the optimal-portfolio calculation also check limits for
the whole portfolio:

- When the relevant asset classes are present, starting capital and margin
  settings provide the total-position and combined bond-position limits.
- The session's foreign-exchange setting provides the combined currency limit.
- The session's short-selling setting provides the combined short-position
  limit.
- Trainer-set net and gross bond interest-rate-risk limits are used when they
  exist. **Net** allows long and short rate risk to offset. **Gross** adds all
  rate risk without offsetting it.
- When PM has no session setting for a check, the assessment uses the engine's
  reference value, adjusted to the session's starting capital. This currently
  covers missing bond interest-rate-risk limits, equity limits, concentrated
  US-dollar risk, commodity risk, option premium, and the fixed adverse-market
  loss limit.

An assessment explicitly set to the **reference framework** keeps the engine's
reference portfolio limits and its stored instrument limits. If no session
instrument limits can be derived, the same reference portfolio limits remain.
The assessment may also contain a saved settings object named **risk_limits**.
Runtime scoring does not read that object and it does not replace these limits.

### Optimal-portfolio source

When PM calculates an optimal portfolio for one linked session, it uses that
session's limits and stores both the computed exposures and a copy of the
instrument limits used. Older assessments may instead use optimal exposures
stored on the shared assessment definition. This is why two sessions using the
same assessment can have different correct optimal portfolios.

### Current unsupported limit source

PM also has a newer **per-asset notional** mode: the trainer enters one absolute
money limit for each asset. The assessment limit resolver does not currently
read those values or reject that session mode. It still reads the older
asset-class percentage settings. Therefore, for such a session, assessment
limits may differ from the limits enforced while trading.

Spot foreign exchange is another special case: trading uses the session's
combined foreign-exchange capacity, while the instrument risk limit is only the
assessment's position-size scale. Other cash, margin, pooled-book, and
short-selling checks can also stop an order before its risk limit is reached.
`;

const GUIDES: Record<
  PillarTwoGuideKey,
  {
    explanation: string;
    points: string;
    subtitle: string;
    title: string;
  }
> = {
  'portfolio-fit': {
    explanation: PORTFOLIO_FIT,
    points: '35% of Pillar 2',
    subtitle: 'How the complete position pattern becomes up to 35 points.',
    title: 'Overall portfolio fit score',
  },
  'position-direction': {
    explanation: POSITION_DIRECTION,
    points: '20% of Pillar 2',
    subtitle: 'How long, short, and flat choices become up to 20 points.',
    title: 'Position direction score',
  },
  'position-size': {
    explanation: POSITION_SIZE,
    points: '20% of Pillar 2',
    subtitle: 'How position amounts become up to 20 points.',
    title: 'Position size score',
  },
  'option-choice': {
    explanation: OPTION_CHOICE,
    points: '10% of Pillar 2',
    subtitle: 'How option use and size become up to 10 points.',
    title: 'Option choice score',
  },
  'own-view-consistency': {
    explanation: OWN_VIEW_CONSISTENCY,
    points: '7.5% of Pillar 2',
    subtitle:
      'How positions matching the user’s sliders become up to 7.5 points.',
    title: 'Own-view consistency score',
  },
  'limit-discipline': {
    explanation: LIMIT_DISCIPLINE,
    points: '7.5% of Pillar 2',
    subtitle: 'How staying within risk limits becomes up to 7.5 points.',
    title: 'Limit discipline score',
  },
  'risk-limits': {
    explanation: RISK_LIMITS,
    points: 'Limit sources',
    subtitle: 'Where each instrument and portfolio limit comes from.',
    title: 'Risk limits',
  },
};

const PillarTwoComponentGuide: React.FC<{ component: PillarTwoGuideKey }> = ({
  component,
}) => {
  const guide = GUIDES[component];

  return (
    <aside
      aria-labelledby={`pillar-two-${component}-title`}
      className="assessment-inspect-assessment__pillar-one-guide"
    >
      <header className="assessment-inspect-assessment__pillar-one-guide-header">
        <div className="assessment-inspect-assessment__pillar-one-guide-kicker">
          <span>
            <InfoCircleOutlined aria-hidden="true" /> Scoring guide
          </span>
          <Tag color="blue">{guide.points}</Tag>
        </div>
        <Typography.Title id={`pillar-two-${component}-title`} level={4}>
          {guide.title}
        </Typography.Title>
        <Typography.Text type="secondary">{guide.subtitle}</Typography.Text>
      </header>

      <div className="assessment-inspect-assessment__pillar-one-guide-body">
        <ReactMarkdown
          rehypePlugins={[rehypeKatex]}
          remarkPlugins={[remarkMath]}
        >
          {guide.explanation}
        </ReactMarkdown>
      </div>
    </aside>
  );
};

export default PillarTwoComponentGuide;
